import React, { createContext, useContext, useState, useCallback, useRef, ReactNode, useEffect } from 'react';
import { Platform } from 'react-native';
import ToastMessage from 'react-native-toast-message';
import { toastConfig, ToastType } from '@/components/common/ToastConfig';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { registerWebModalManager, registerWebToastManager } from '@/utils/common/alert';
import { registerGlobalToastHandler, unregisterGlobalToastHandler, GlobalToast } from '@/utils/common/toastBridge';

/**
 * On web, react-native-web's Modal renders via a portal appended to document.body,
 * which stacks above the normal React tree. We render Toast through its own portal
 * with a high z-index so it always appears above modals.
 */
const WebToastPortal: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';
    el.style.right = '0';
    el.style.zIndex = '99999';
    el.style.pointerEvents = 'none';
    document.body.appendChild(el);
    setPortalElement(el);
    return () => {
      document.body.removeChild(el);
    };
  }, []);

  if (!portalElement) return null;

  const { createPortal } = require('react-dom');
  return createPortal(children, portalElement);
};

const ToastPortal: React.FC<{ children: ReactNode }> = ({ children }) => {
  if (Platform.OS !== 'web') return <>{children}</>;
  return <WebToastPortal>{children}</WebToastPortal>;
};

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  autoHide?: boolean;
  duration?: number;
  position?: 'top' | 'bottom';
}

interface ToastContextType {
  toasts: ToastItem[];
  showToast: (options: Omit<ToastItem, 'id'>) => void;
  showSuccess: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showError: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showWarning: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showInfo: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => void;
  showLoading: (message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => string;
  hideLoading: (id?: string) => void;
  hideToast: (id: string) => void;
  hideAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

interface ConfirmModalState {
  visible: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  // maxToasts kept for API compatibility but no longer used (library shows 1 at a time)
  maxToasts: _maxToasts = 3
}) => {
  const currentToastTypeRef = useRef<ToastType | null>(null);
  const loadingToastIdsRef = useRef<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    title: '',
    message: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((options: Omit<ToastItem, 'id'>) => {
    const autoHide = options.autoHide ?? true;
    const duration = options.duration ?? 4000;
    const position = options.position ?? 'top';

    currentToastTypeRef.current = options.type;
    ToastMessage.show({
      type: options.type,
      text1: options.message,
      autoHide,
      visibilityTime: duration,
      position,
      onHide: () => {
        currentToastTypeRef.current = null;
      },
    });
  }, []);

  // Bridge: let non-React modules (baseQuery/BaseAPI) raise toasts (RES-102).
  useEffect(() => {
    const handler = ({ type, message }: GlobalToast) => showToast({ type, message });
    registerGlobalToastHandler(handler);
    return () => unregisterGlobalToastHandler(handler);
  }, [showToast]);

  const showSuccess = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'success', message, ...options });
  }, [showToast]);

  const showError = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'error', message, ...options });
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'warning', message, ...options });
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>) => {
    showToast({ type: 'info', message, ...options });
  }, [showToast]);

  const showLoading = useCallback((message: string, options?: Partial<Omit<ToastItem, 'id' | 'type' | 'message'>>): string => {
    const id = generateId();
    loadingToastIdsRef.current.add(id);
    currentToastTypeRef.current = 'loading';

    ToastMessage.show({
      type: 'loading',
      text1: message,
      autoHide: false,
      position: options?.position ?? 'top',
      onHide: () => {
        currentToastTypeRef.current = null;
      },
    });

    return id;
  }, [generateId]);

  const hideLoading = useCallback((id?: string) => {
    if (id) {
      loadingToastIdsRef.current.delete(id);
    } else {
      loadingToastIdsRef.current.clear();
    }

    // Only hide if the current toast is still a loading toast
    if (currentToastTypeRef.current === 'loading') {
      currentToastTypeRef.current = null;
      ToastMessage.hide();
    }
  }, []);

  const hideToast = useCallback((_id: string) => {
    ToastMessage.hide();
    currentToastTypeRef.current = null;
  }, []);

  const hideAllToasts = useCallback(() => {
    loadingToastIdsRef.current.clear();
    currentToastTypeRef.current = null;
    ToastMessage.hide();
  }, []);

  // Register the modal and toast managers with the alert utility (web only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      registerWebModalManager({
        showConfirmModal: (config) => {
          setConfirmModal({
            visible: true,
            title: config.title,
            message: config.message,
            confirmText: config.confirmText,
            cancelText: config.cancelText,
            onConfirm: config.onConfirm,
            onCancel: config.onCancel || (() => {}),
          });
        },
      });

      registerWebToastManager({
        showInfo: (message) => showInfo(message),
        showSuccess: (message) => showSuccess(message),
        showError: (message) => showError(message),
        showWarning: (message) => showWarning(message),
      });
    }
  }, [showInfo, showSuccess, showError, showWarning]);

  const contextValue: ToastContextType = {
    toasts: [], // No longer used — library manages its own state
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    hideLoading,
    hideToast,
    hideAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastPortal>
        <ToastMessage config={toastConfig} topOffset={60} />
      </ToastPortal>

      {/* Web confirmation modal */}
      {Platform.OS === 'web' && (
        <WebConfirmModal
          visible={confirmModal.visible}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          onConfirm={() => {
            setConfirmModal(prev => ({ ...prev, visible: false }));
            confirmModal.onConfirm();
          }}
          onCancel={() => {
            setConfirmModal(prev => ({ ...prev, visible: false }));
            confirmModal.onCancel();
          }}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Convenience hook for error handling
export const useErrorToast = () => {
  const { showError } = useToast();

  const showApiError = useCallback((error: any, fallbackMessage?: string) => {
    // Import error handling utility
    import('@/utils/admin/errorHandling').then(({ getErrorMessage, shouldShowToast }) => {
      if (shouldShowToast(error)) {
        const message = getErrorMessage(error) || fallbackMessage || 'An error occurred';
        showError(message);
      }
    });
  }, [showError]);

  return { showApiError, showError };
};
